import React from "react";
import { makeStyles, tokens, typographyStyles, Tooltip } from "@fluentui/react-components";
import { ProjectFileNode } from "../../../models/shared/ProjectFileNode";
import { getAttachmentIcon } from "../../shared/attachments/attachmentList/FileTypeIcons";
import { AttachmentVisual } from "../../shared/attachments/attachmentList/AttachmentListComponentTypes";
import { NewformaSpinner } from "../../_base/Spinner";
import { NewformaCheckbox } from "../../_base/Checkbox";
import { NewformaLink } from "../../_base/Link";

export interface ProjectFilesBrowserRowProps {
    node: ProjectFileNode;
    index: number;
    isSelected: boolean;
    /** Folder-only: some (but not all) of the folder's contents are selected. Renders the checkbox as indeterminate. */
    isPartiallySelected?: boolean;
    isLoading?: boolean;
    disabled: boolean;
    hideCheckbox?: boolean;
    checkboxDisabled?: boolean;
    /** Tooltip shown on a selected folder's name explaining that it must be unselected to browse. */
    selectedFolderTooltip?: string;
    onCheckboxChange: (node: ProjectFileNode, checked: boolean) => void;
    onFolderClick: (node: ProjectFileNode) => void;
}

const useStyles = makeStyles({
    row: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        minHeight: "32px",
        paddingLeft: "4px",
        paddingRight: "4px",
        borderRadius: tokens.borderRadiusMedium,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    icon: {
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        color: tokens.colorBrandForeground1,
    },
    folderName: {
        ...typographyStyles.caption1,
        color: tokens.colorBrandForegroundLink,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        flex: 1,
        cursor: "pointer",
    },
    fileName: {
        ...typographyStyles.caption1,
        color: tokens.colorNeutralForeground3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        minWidth: 0,
        flex: 1,
    },
    checkboxSlot: {
        width: "20px",
        height: "20px",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
    },
});

/** Maps a ProjectFileNode to an AttachmentVisual so getAttachmentIcon can be reused. */
function nodeToAttachmentVisual(node: ProjectFileNode): AttachmentVisual {
    if (node.type === "folder") {
        return { type: "folder", key: node.id, name: node.name };
    }
    const extension = node.name.includes(".") ? node.name.split(".").pop() ?? "" : "";
    return { type: "file", key: node.id, name: node.name, extension };
}

export const ProjectFilesBrowserRow: React.FC<ProjectFilesBrowserRowProps> = ({
    node,
    index,
    isSelected,
    isPartiallySelected = false,
    isLoading = false,
    disabled,
    hideCheckbox = false,
    checkboxDisabled,
    selectedFolderTooltip,
    onCheckboxChange,
    onFolderClick,
}) => {
    const styles = useStyles();
    const visual = nodeToAttachmentVisual(node);

    const isFolder = node.type === "folder";
    // Selected folders cannot be browsed — the user must uncheck them first.
    const isNavigationBlocked = isFolder && isSelected;
    // Fluent Checkbox tri-state: true (full), "mixed" (partial), false (none).
    const checkedState: boolean | "mixed" = isSelected ? true : isPartiallySelected ? "mixed" : false;

    const renderFolderLink = () => {
        if (isNavigationBlocked) {
            // Render as plain text (not a link): selected folders are not browsable.
            // A span is used instead of a disabled button so hover events still fire
            // and the explanatory tooltip can be shown.
            const blockedName = (
                <span
                    className={styles.fileName}
                    data-testid={`project-file-folder-link-${index}`}
                    aria-disabled="true"
                >
                    {node.name}
                </span>
            );
            if (selectedFolderTooltip) {
                return (
                    <Tooltip content={selectedFolderTooltip} relationship="description" appearance="inverted" withArrow>
                        {blockedName}
                    </Tooltip>
                );
            }
            return blockedName;
        }
        return (
            <NewformaLink
                className={styles.folderName}
                onClick={() => onFolderClick(node)}
                disabled={disabled || isLoading}
                dataTestId={`project-file-folder-link-${index}`}
            >
                {node.name}
            </NewformaLink>
        );
    };

    return (
        <div className={styles.row} data-testid={`project-file-row-${index}`}>
            <div className={styles.checkboxSlot}>
                {hideCheckbox ? null : isLoading ? (
                    <NewformaSpinner size="extra-tiny" />
                ) : (
                    <NewformaCheckbox
                        checked={checkedState}
                        disabled={disabled || checkboxDisabled}
                        onChange={(_e, data) => onCheckboxChange(node, !!data.checked)}
                        ariaLabel={node.name}
                        dataTestId={`project-file-checkbox-${index}`}
                    />
                )}
            </div>
            <span className={styles.icon}>{getAttachmentIcon(visual)}</span>
            {isFolder ? (
                renderFolderLink()
            ) : (
                <span className={styles.fileName} title={node.name}>
                    {node.name}
                </span>
            )}
        </div>
    );
};
